'use client'

import {useParams, usePathname, useRouter as useAppRouter} from 'next/navigation'
import {UrlObject} from 'url'

import {Locale} from '../constants'
import {useLanguage} from '../i18n/language'
import {routing} from '../i18n/routing'

type NavigateOptions = {
  scroll?: boolean
  // the locale to navigate into; the current one when omitted
  locale?: Locale
}

export const getWithoutLocale = (path: string) => {
  const withoutLocale = path.replace(
    new RegExp(`^/(${Object.values(Locale).join('|')})(?=/|$)`, 'i'),
    '',
  )
  return withoutLocale || '/'
}

/**
 * Locale-aware navigation: everything that can be derived from the pathname and the dynamic
 * route params alone.
 *
 * Deliberately does NOT call `useSearchParams()`. That hook opts a component out of static
 * rendering unless it sits under a `<Suspense>` boundary, and it re-renders every subscriber on
 * any query-string change — neither of which most callers need. Reach for `useRouter()` only
 * when you genuinely read `query` or `asPath`; otherwise use this.
 */
export function useNavigation() {
  const appRouter = useAppRouter()
  const pathname = getWithoutLocale(usePathname() || '/')
  const params = useParams() || {}

  const language = useLanguage()

  const createPath = (url: UrlObject | string, newLocale?: Locale) => {
    if (typeof url === 'string' && url.includes('://')) {
      // shortcut for changing domain, e.g. from Preferences.setDomain('https://tramatm.sk')
      if (newLocale) {
        // try to switch the url to use the newLocale
        const urlObject = new URL(url)
        urlObject.pathname = `/${newLocale}${getWithoutLocale(urlObject.pathname)}`
        return urlObject.toString()
      } else {
        return url
      }
    }

    const path =
      typeof url === 'string'
        ? getWithoutLocale(url)
        : (() => {
            const qs = new URLSearchParams(
              url.query
                ? Object.fromEntries(
                    Object.entries(url.query).flatMap(([k, v]) =>
                      Array.isArray(v) ? v.map((val) => [k, val]) : [[k, String(v ?? '')]],
                    ),
                  )
                : [],
            ).toString()
            const resolvedPathname = url.pathname ? getWithoutLocale(url.pathname) : pathname
            return qs ? `${resolvedPathname}?${qs}` : resolvedPathname
          })()
    const targetLocale = newLocale ?? language

    return (targetLocale === routing.defaultLocale ? '' : `/${targetLocale}`) + path
  }

  // Both resolve straight away, as the App Router reports no completion. They return a Promise
  // only so that callers awaiting them stay valid.
  const push = (url: UrlObject | string, {scroll, locale}: NavigateOptions = {}) => {
    appRouter.push(createPath(url, locale), {scroll})
    return Promise.resolve()
  }

  const replace = (url: UrlObject | string, {scroll, locale}: NavigateOptions = {}) => {
    appRouter.replace(createPath(url, locale), {scroll})
    return Promise.resolve()
  }

  const getRoute = () => {
    // Replace dynamic segment values with their param names
    // e.g. /users/123 → /users/[id]
    const reversedParams = Object.entries(params).reduce<Record<string, string>>(
      (acc, [key, value]) => {
        if (Array.isArray(value)) {
          // Catch-all: [...slug] → value is ['a', 'b', 'c']
          acc[value.join('/')] = `...${key}`
        } else {
          acc[value] = key
        }
        return acc
      },
      {},
    )

    const segments = pathname.split('/').map((segment) => {
      if (segment in reversedParams) {
        const placeholder = reversedParams[segment]
        return `[${placeholder}]`
      }
      return segment
    })

    return segments.join('/')
  }

  return {
    push,
    replace,
    reload: () => {
      window.location.reload()
    },
    // The path as rendered, without the locale prefix or the query string.
    pathname,
    // The same path with dynamic segments left as placeholders (`/class-assist/[industry]`), so
    // that every page of one route shares it - for analytics page names and per-route lookups.
    route: getRoute(),
  }
}
